import { ExamEventErrorCode } from '@exam-center/graphql';
import {
  CancelOutlinedIcon,
  CheckCircleOutlineRoundedIcon,
  type GridColDef,
  Label,
  Text,
  EllipsisText,
} from '@shared/components';
import dayjs from 'dayjs';

import { formatThaiDate } from '../../utils/format-thai-date';
import { formatThaiDateTime } from '../recurring-exam-event-creation-review-details';

export interface RecurringExamEventCreationReviewListItem {
  id: string;
  examDate: Date;
  registrationStartDate: Date;
  registrationEndDate: Date;
  createAble: boolean;
  reason: ExamEventErrorCode[];
}

function mapErrorToMessage(errorCodes: ExamEventErrorCode[]): string {
  if (errorCodes.length === 0) return '-';

  const priorityOrder: ExamEventErrorCode[] = [
    ExamEventErrorCode.RegistrationEndedAtShouldBeLessThanExamStartedAtAtLeast_30Minutes,
    ExamEventErrorCode.RegistrationEndedAtShouldBeGreaterThanRegistrationStartedAt,
    ExamEventErrorCode.RegistrationEndedAtShouldBeGreaterThanNowAtLeast_15Minutes,
    ExamEventErrorCode.SomeRoomsAreUnavailable,
    ExamEventErrorCode.SomeProctorsAreUnavailable,
  ];

  const highestPriorityError = priorityOrder.find((code) => errorCodes.includes(code));

  switch (highestPriorityError) {
    case ExamEventErrorCode.RegistrationEndedAtShouldBeLessThanExamStartedAtAtLeast_30Minutes:
      return 'วันที่เปิดรับสมัครต้องน้อยกว่าวันสอบ';
    case ExamEventErrorCode.RegistrationEndedAtShouldBeGreaterThanRegistrationStartedAt:
      return 'วันปิดรับสมัครต้องมากกว่าวันเปิดรับสมัคร';
    case ExamEventErrorCode.RegistrationEndedAtShouldBeGreaterThanNowAtLeast_15Minutes:
      return 'ไม่สามารถปิดรับสมัครย้อนหลังได้';
    case ExamEventErrorCode.SomeRoomsAreUnavailable:
      return 'ห้องสอบไม่ว่างในวันและเวลาที่เลือก';
    case ExamEventErrorCode.SomeProctorsAreUnavailable:
      return 'ผู้คุมสอบไม่ว่างในวันและเวลาที่เลือก';
    default:
      return '-';
  }
}

export const columns: GridColDef<RecurringExamEventCreationReviewListItem>[] = [
  {
    field: 'date',
    headerName: 'วัน',
    sortable: false,
    width: 100,
    renderCell: ({ row }) => {
      return <Text>{dayjs(row.examDate).locale('th').format('dd')}</Text>;
    },
  },
  {
    field: 'examDate',
    headerName: 'วันที่สอบ',
    sortable: false,
    width: 120,
    renderCell: ({ row }) => {
      return <Text>{formatThaiDate(row.examDate)}</Text>;
    },
  },
  {
    field: 'registrationStartDate',
    headerName: 'วันที่เปิดรับสมัคร',
    sortable: false,
    width: 180,
    renderCell: ({ row }) => {
      return <Text>{formatThaiDateTime(row.registrationStartDate)}</Text>;
    },
  },
  {
    field: 'registrationEndDate',
    headerName: 'วันที่ปิดรับสมัคร',
    sortable: false,
    width: 180,
    renderCell: ({ row }) => {
      return <Text sx={{ fontSize: '16px' }}>{formatThaiDateTime(row.registrationEndDate)}</Text>;
    },
  },
  {
    field: 'createAble',
    headerName: 'สถานะ',
    sortable: false,
    width: 120,
    renderCell: ({ row }) => {
      return (
        <Label
          variant='outlined'
          color={row.createAble ? 'success' : 'error'}
          sx={{
            borderRadius: (theme) => theme.shape.rounded(7),
            width: 'auto',
          }}
          startIcon={
            row.createAble ? (
              <CheckCircleOutlineRoundedIcon sx={{ fontSize: '16px' }} />
            ) : (
              <CancelOutlinedIcon sx={{ fontSize: '16px' }} />
            )
          }
        >
          <Text
            variant='body2Semibold'
            sx={{
              color: (theme) =>
                row.createAble ? theme.palette.success.main : theme.palette.error.main,
            }}
          >
            {row.createAble ? 'สร้างได้' : 'สร้างไม่ได้'}
          </Text>
        </Label>
      );
    },
  },
  {
    field: 'reason',
    headerName: 'หมายเหตุ',
    sortable: false,
    flex: 1,
    renderCell: ({ row }) => {
      return <EllipsisText>{mapErrorToMessage(row.reason)}</EllipsisText>;
    },
  },
];
